const FORMATTER_CACHE = new Map();

class AnnouncementCountdown extends HTMLElement {
  #timerId;
  #endTimestamp;

  connectedCallback() {
    if (this.dataset.countdownEnabled !== 'true') return;

    const endDate = this.dataset.endDate || '';
    const endTime = this.dataset.endTime || '23:59';
    const timeZone = this.dataset.timeZone || 'America/Edmonton';

    this.#endTimestamp = zonedDateTimeToUtc(endDate, endTime, timeZone);

    if (!Number.isFinite(this.#endTimestamp)) {
      this.dataset.countdownInvalid = 'true';
      const timer = this.querySelector('[data-countdown-timer]');
      if (timer instanceof HTMLElement) timer.hidden = true;
      console.warn(
        `[announcement-countdown] Invalid end date/time: ${endDate} ${endTime} (${timeZone}). ` +
          'Use YYYY-MM-DD and HH:MM in 24-hour time.'
      );
      return;
    }

    this.#tick();
  }

  disconnectedCallback() {
    window.clearTimeout(this.#timerId);
  }

  #tick = () => {
    const remaining = this.#endTimestamp - Date.now();

    if (remaining <= 0) {
      this.#expire();
      return;
    }

    // Round up so the countdown does not show 0 minutes while time remains.
    const totalMinutes = Math.ceil(remaining / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;

    this.#setText('[data-countdown-days]', String(days).padStart(2, '0'));
    this.#setText('[data-countdown-hours]', String(hours).padStart(2, '0'));
    this.#setText('[data-countdown-minutes]', String(minutes).padStart(2, '0'));

    const timer = this.querySelector('[data-countdown-timer]');
    if (timer instanceof HTMLElement) {
      timer.setAttribute(
        'aria-label',
        `${days} day${days === 1 ? '' : 's'}, ${hours} hour${hours === 1 ? '' : 's'}, ` +
          `${minutes} minute${minutes === 1 ? '' : 's'} remaining`
      );
    }

    // Refresh at the next minute boundary, with a small buffer for clock drift.
    const millisecondsIntoMinute = Date.now() % 60000;
    const nextUpdate = Math.max(1000, 60000 - millisecondsIntoMinute + 50);
    this.#timerId = window.setTimeout(this.#tick, Math.min(nextUpdate, remaining));
  };

  #expire() {
    window.clearTimeout(this.#timerId);
    this.dataset.expired = 'true';

    const activeValues = this.querySelector('[data-countdown-values]');
    const expiredLabel = this.querySelector('[data-countdown-expired-label]');
    const timer = this.querySelector('[data-countdown-timer]');
    const button = this.querySelector('[data-countdown-button]');

    if (activeValues instanceof HTMLElement) activeValues.hidden = true;

    if (timer instanceof HTMLElement) {
      timer.setAttribute('aria-label', this.dataset.expiredText || 'Offer ended');

      if (this.dataset.hideTimerAfterExpiry === 'true') {
        timer.hidden = true;
      } else if (expiredLabel instanceof HTMLElement) {
        expiredLabel.hidden = false;
        expiredLabel.textContent = this.dataset.expiredText || 'Offer ended';
      }
    }

    if (button instanceof HTMLElement && this.dataset.hideButtonAfterExpiry === 'true') {
      button.hidden = true;
    }

    // Disable every link inside the announcement, including links inside rich text.
    this.querySelectorAll('a').forEach((link) => {
      const href = link.getAttribute('href');
      if (href) link.dataset.originalHref = href;
      link.removeAttribute('href');
      link.setAttribute('aria-disabled', 'true');
      link.setAttribute('tabindex', '-1');
    });
  }

  #setText(selector, value) {
    const element = this.querySelector(selector);
    if (element) element.textContent = value;
  }
}

if (!customElements.get('announcement-countdown')) {
  customElements.define('announcement-countdown', AnnouncementCountdown);
}

/**
 * Convert a wall-clock date/time in an IANA timezone into a UTC timestamp.
 * This keeps the configured deadline fixed to Calgary time, including DST.
 *
 * @param {string} dateString YYYY-MM-DD
 * @param {string} timeString HH:MM (24-hour)
 * @param {string} timeZone IANA timezone, e.g. America/Edmonton
 * @returns {number} UTC epoch milliseconds, or NaN when invalid
 */
function zonedDateTimeToUtc(dateString, timeString, timeZone) {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(timeString);

  if (!dateMatch || !timeMatch) return Number.NaN;

  const year = Number(dateMatch[1]);
  const month = Number(dateMatch[2]);
  const day = Number(dateMatch[3]);
  const hour = Number(timeMatch[1]);
  const minute = Number(timeMatch[2]);

  if (
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > 31 ||
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return Number.NaN;
  }

  const utcWallClock = Date.UTC(year, month - 1, day, hour, minute, 0);
  let timestamp = utcWallClock;

  // Iteratively resolve the timezone offset at the target instant.
  for (let index = 0; index < 4; index += 1) {
    const offset = getTimeZoneOffset(timestamp, timeZone);
    const nextTimestamp = utcWallClock - offset;

    if (Math.abs(nextTimestamp - timestamp) < 1000) {
      timestamp = nextTimestamp;
      break;
    }

    timestamp = nextTimestamp;
  }

  // Reject impossible dates/times, including DST-skipped local times.
  const roundTrip = getZonedParts(timestamp, timeZone);
  if (
    roundTrip.year !== year ||
    roundTrip.month !== month ||
    roundTrip.day !== day ||
    roundTrip.hour !== hour ||
    roundTrip.minute !== minute
  ) {
    return Number.NaN;
  }

  return timestamp;
}

function getTimeZoneOffset(timestamp, timeZone) {
  const parts = getZonedParts(timestamp, timeZone);
  const representedAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second
  );
  const timestampToSecond = Math.floor(timestamp / 1000) * 1000;
  return representedAsUtc - timestampToSecond;
}

function getZonedParts(timestamp, timeZone) {
  let formatter = FORMATTER_CACHE.get(timeZone);

  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
    FORMATTER_CACHE.set(timeZone, formatter);
  }

  const values = {};
  formatter.formatToParts(new Date(timestamp)).forEach((part) => {
    if (part.type !== 'literal') values[part.type] = Number(part.value);
  });

  return {
    year: values.year,
    month: values.month,
    day: values.day,
    hour: values.hour,
    minute: values.minute,
    second: values.second,
  };
}